"use client";

import React, { useState, useEffect } from "react";
import { Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { CartIcon } from "@/components/shop/CartIcon";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="204718" className="flex gap-4">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 items-center justify-center px-2 py-2 text-sm font-default font-medium text-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50">
                <span className="relative after:absolute after:-bottom-1 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all group-hover:after:w-full">
                  {item.label}
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 mt-8">
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-3 text-lg font-default font-medium text-foreground hover:text-primary transition-colors border-b border-border/40">
            {item.label}
          </Link>
        </div>)}
      <Button variant="outline" className="mt-6 w-full font-default border-primary/50 text-foreground hover:bg-primary/10">
        Member Access
      </Button>
    </div>;
}
export default function PrimaryNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="global-navigation-header" className="sticky top-0 z-50 w-full flex flex-col dark">
      {}
      <div className={cn("w-full px-4 py-1 flex justify-between items-center transition-all duration-300", isScrolled ? "bg-secondary/90 backdrop-blur-md" : "bg-secondary")}>
        <Button variant="ghost" size="sm" className="h-7 text-xs font-default text-foreground hover:bg-primary/20">
          Priority Support
        </Button>
        <Select defaultValue="en">
          <SelectTrigger className="h-7 text-xs border-none bg-transparent shadow-none focus:ring-0 w-auto gap-1 text-foreground hover:bg-primary/20 rounded-md px-2">
            <Globe className="w-3.5 h-3.5 text-primary" />
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="en" className="text-xs font-default">English</SelectItem>
            <SelectItem value="es" className="text-xs font-default">Español</SelectItem>
            <SelectItem value="fr" className="text-xs font-default">Français</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {}
      <div className={cn("w-full px-4 md:px-8 py-4 relative flex flex-col items-center justify-center transition-all duration-300", isScrolled ? "bg-background/70 backdrop-blur-md" : "bg-background")}>
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex justify-center items-center">
            <h1 className="text-lg font-bold text-foreground font-default text-center">ApexRig Tech</h1>
          </div>

          <div className={cn("w-full flex justify-center items-center transition-all duration-300", isScrolled ? "bg-transparent" : "bg-transparent")}>
            <MenuProvider menu_id="22">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        {}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <Button variant="outline" size="sm" className="hidden sm:flex font-default border-primary/30 text-foreground hover:bg-primary/10 hover:border-primary/60 transition-all">
            Member Access
          </Button>
          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary" />
        </div>
      </div>
    </section>;
}